import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import pluralize from 'pluralize';
import { useNotesQueries } from './useNotesQueries';
import * as styles from './NotesButton.module.scss';
import NotesDialog from './NotesDialog';
import { Button, Icon, IconList } from 'components/core';
import { QueryProvider } from 'query/QueryProvider';
import {
  createNote as createNoteQuery,
  fetchNotes as fetchNotesQuery,
} from 'query/study/Notes';
import { Study } from 'pages/StudyList/StudyList';

export type NotesButtonProps = {
  // Initial because it will be updated by use of the modal
  initialNotesCount: number;
  reviewStudyId: Study['id'];
  title: string | null;
  noteCategory: string;

  fetchNotes?: (reviewStudyId: Study['id']) => Promise<any>;
  createNote?: (
    note: string,
    category: string,
    reviewStudyId: Study['id']
  ) => Promise<any>;
};

export const NotesButton = ({
  initialNotesCount,
  reviewStudyId,
  title,
  noteCategory,
  createNote = createNoteQuery,
  fetchNotes = fetchNotesQuery,
}: NotesButtonProps) => {
  const [isOpen, setOpen] = useState(false);
  const [isQueryEnabled, setIsQueryEnabled] = useState(false);

  // only enable when user opens for the first time
  useEffect(() => {
    if (isOpen) {
      setIsQueryEnabled(true);
    } else {
      // Don't need to keep polling if the modal is closed
      setIsQueryEnabled(false);
    }
  }, [isOpen]);

  const {
    isFetching,
    notes,
    error,
    mutate,
    isCreating,
    mutationError,
    notesCount,
    invalidateQueries,
  } = useNotesQueries({
    reviewStudyId,
    fetchNotes,
    createNote,
    enabled: isQueryEnabled,
    intialCount: initialNotesCount,
  });

  const showNotesDialog = () => {
    setOpen(true);
  };

  return (
    <QueryProvider>
      <Button
        type="brand"
        variant="ghost"
        onClick={showNotesDialog}
        data-pendo-key="notes-button"
      >
        <span className={classNames(notesCount > 0 && styles.ActivityDot)}>
          <Icon icon={IconList.light.faMessageLines} />
        </span>
        {notesCount > 0
          ? `${notesCount} ${pluralize('Note', notesCount)}`
          : 'Note'}
      </Button>
      {isOpen && (
        <NotesDialog
          notes={notes}
          onClose={() => setOpen(false)}
          title={title}
          noteCategory={noteCategory}
          onSubmit={(args) => {
            mutate({ note: args.note, category: args.category });
          }}
          isFetching={isFetching}
          isCreating={isCreating}
          fetchError={error}
          mutationError={mutationError}
          invalidateQueries={invalidateQueries}
        />
      )}
    </QueryProvider>
  );
};
